// ThemeContext.js
import { createContext, useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

export const ColorModeContext = createContext({ toggleColorMode: () => { } });

export default function ThemeContextProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved === 'light' || saved === 'dark' ? saved : 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const colorMode = useMemo(() => ({
    toggleColorMode: () => {
      setMode(prev => (prev === 'light' ? 'dark' : 'light'));
    },
  }), []);

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      background: {
        default: mode === 'light' ? '#FAFAFA' : '#131313',
        paper: mode === 'light' ? '#FFFFFF' : '#333333',
      },
      text: {
        primary: mode === 'light' ? '#131313' : '#FFFFFF',
        alt: mode === 'light' ? '#E5E7EB' : '#cf230cff',
      },
      border: {
        alt: mode === 'light' ? '#E5E7EB' : '#cf230cff',
      },
      primary: {
        main: mode === 'light' ? '#D47200' : '#FDAC4E',
        contrastText: mode === 'light' ? '#FFFFFF' : '#333333',
        alt: mode === 'light' ? '#E5E7EB' : '#cf230cff',
      },
      secondary: {
        main: mode === 'light' ? '#333333' : '#FAFAFA',
        reverse: mode === 'light' ? '#FAFAFA' : '#333333',
      },
      customPagination: {
        main: mode === 'light' ? '#004394' : '#549AEF',
        secondary: mode === 'light' ? '#549AEF' : '#004394',
        contrastText: mode === 'light' ? '#FFFFFF' : '#131313',
        inactiveText: mode === 'light' ? '#131313' : '#FFFFFF',
      },
      detailViewBox: {
        background: mode === 'light' ? '#E9F3FF' : '#192F49',
        border: mode === 'light' ? '2px solid #004394' : '2px solid #549AEF'
      },
      box: {
        background: mode === 'light' ? '#FFFFFF' : '#545454',
      },
      card: {
        product: mode === 'light' ? '#FFF1E1' : '#5D3201',
      },
      accordion: {
        color: mode === 'light' ? '#004394' : '#549AEF',
        backgroundColor: mode === 'light' ? "#F6F6F6" : '#2F2F2F',
        border: mode === 'light' ? "1px solid rgba(0, 0, 0, 0.25)" : "1px solid rgba(255, 255, 255, 0.6)"
      },
    },
    components: {
      MuiButton: {
        variants: [
          {
            props: { variant: 'alt' },
            style: mode === 'light' ? ({
              backgroundColor: '#FAFAFA',
              color: '#131313',
              border: "1px solid rgba(0, 0, 0, 0.25)",
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#D47200',
              },
            }) : (
              {
                backgroundColor: '#333',
                color: '#FAFAFA',
                border: "1px solid rgba(255, 255, 255, 0.60)",
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#D47200',
                }
              }
            ),
          }
        ]
      },
    },
    typography: {
      fontFamily: '"Helvetica Neue"',
      // allVariants: {
      //   textTransform: 'capitalize'
      // }
    },
    pannelloDettagli: {
      backgroundColor: mode === 'light' ? '#FAFAFA' : '#131313'
    },
    mappatavoli: {
      delete_button: {
        backgroundColor: mode === 'light' ? '#E00C0C' : '#FF8C8C',
      },
      add_edit_button: {
        backgroundColor: mode === 'light' ? '#D47200' : '#FDAC4E',
      },
    },
    checkout: {
      cancel_button: {
        backgroundColor: '#FF5858',
        height: 48,
        borderRadius: "4px",
        color: "#131313",
      },
      subtotal_button: {
        backgroundColor: mode === 'light' ? '#FFD18A' : '#FECA7B',
        height: 48,
        borderRadius: "4px",
        color: "#131313",
      },
      closeCheckout_button: {
        backgroundColor: mode === 'light' ? '#64D7B1' : '#3A7360',
        color: mode === 'light' ? '#131313' : '#FFFFFF',
        borderRadius: "4px",
        height: 48,
      },
      invoiceCheckout_button: {
        backgroundColor: mode === 'light' ? '#B9CFFF' : '#5A72A8',
        color: mode === 'light' ? "#131313" : "#FFFFFF",
        borderRadius: "4px",
        height: 48,
      },
      orange_button: {
        backgroundColor: mode === 'light' ? '#FF9437' : '#DA8234',
        color: "#000000",
      },
      cyan_button: {
        backgroundColor: mode === 'light' ? '#B9CFFF' : '#5A72A8',
        color: mode === 'light' ? "#131313" : "#FFFFFF",
      },
      blue_button: {
        backgroundColor: mode === 'light' ? '#004394' : '#549AEF',
        color: mode === 'light' ? "#FFFFFF" : "#131313",
        py: 2
      },
      keypad_button: {
        backgroundColor: mode === 'light' ? '#E5EDFF' : '#2D3850',
        color: mode === 'light' ? "#131313" : "#FFFFFF",
      },
      typography_button: {
        fontSize: {
          xs: 8,
          sm: 10,
          md: 12,
          lg: 14,
          xl: 16
        },
        fontWeight: 500,
        whitespace: "nowrap",
        py: 1
      },
      orange_outline_button: {
        border: mode === 'light' ? 'solid #FF9437 1px' : 'solid #DA8234 1px',
        color: mode === 'light' ? '#FF9437' : '#DA8234',
        borderRadius: "8px",
        height: "40px",
      },
      display_checkout: {
        borderRadius: 2,
        border: mode === 'light' ? "solid #FFFFFF 1px" : "solid #00000040 1px",
      },
      product_button: {
        backgroundColor: mode === 'light' ? '#FBFBFB' : '#131313',
        color: mode === 'light' ? "#131313" : "#FFFFFF",
        border: mode === 'light' ? "solid #00000040 1px" : "solid #FFFFFF99 1px",
        borderRadius: 1,
      },
      paper_checkout: {
        backgroundColor: mode === 'light' ? '#FFFFFF' : '#333333',
        borderRadius: "16px",
      },
      paper_quantity: {
        backgroundColor: mode === 'light' ? '#E5EDFF' : '#2D3850',
        color: mode === 'light' ? "#131313" : "#FFFFFF",
        borderRadius: "10px",
      },
      card_checkout: {
        backgroundColor: mode === 'light' ? '#FBFBFB' : '#131313',
        color: mode === 'light' ? "#131313" : "#FFFFFF",
        border: mode === 'light' ? "solid #00000040 1px" : "solid #FFFFFF99 1px",
        borderRadius: "8px",
        minHeight: "86px",
        my: 1,
        alignContent: "center",
        px: 1
      },
      modal_checkout: {
        backgroundColor: mode === 'light' ? '#FBFBFB' : '#333333',
        color: mode === 'light' ? "#131313" : "#FAFAFA",
        border: mode === 'light' ? "solid #00000040 1px" : "solid #FFFFFF99 1px",
        borderRadius: "8px",
        minHeight: "100px",
        alignContent: "center",
        py: "16px",
      },
      closeModalCheckout_button: {
        color: mode === 'light' ? "#131313" : "#FFFFFF",
        textTransform: 'capitalize',
        fontSize: 16,
        width: 100,
        height: 44,
        gap: "8.7px",
        opacity: 1,
        borderRadius: "6.53px",
        py: "8.7px",
        px: "26.1px",

      },
      closeModalCheckout_paper: {
        width: 163.5,
        height: 68,
        border: "1.09px solid #FFFFFF99",
        borderRadius: "6.53px",
        borderWidth: "1.09px",
        padding: "12px",

      },
      closeModalCheckout_paperTypography1: {
        fontSize: 12,
        color: mode === 'light' ? "#303030" : "#B5B5B5",

      },
      closeModalCheckout_paperTypography2: {
        fontWeight: 500,
        fontStyle: "Medium",
        fontSize: 12,
        color: mode === 'light' ? "#131313" : "#FFFFFF",
      },
      animation_typography: {
        whiteSpace: "nowrap",
        display: "inline-block",
      },
      color: "#131313",
      borderRadius: "4px"
    }
  }), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
